import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { catchError, combineLatest, of, Subscription } from 'rxjs';

import { UserAccountFacade } from '@spartacus/user/account/root';

import { SdTodoCardComponent, ITodoCard, ITodoCardStatus } from '@seb/web-design-system/todo-card';
import { BaseStoreService, MyAccountService } from '@seb/web-common/core/services';
import { Asset } from '@seb/web-common/core/models/asset.model';
import { Optin } from '@seb/web-common/core/models';

import {
  BrandThemesEnum, SdBorderWrapperComponent, SdCopyToClipboardDirective, SdIconComponent,
  SdTitleComponent, SdParsePipe, SdTruncatePipe, SdLoaderComponent, SdHtmlComponent
} from '@seb/web-design-system/core';

const TODO_CARD_PURCHASE_IMG = '/assets/images/asset-todo-card-purchase.jpg';
const TODO_CARD_NEWSLETTER_IMG = '/assets/images/asset-todo-card-newsletter.jpg';

@Component({
  selector: 'wc-my-account-sponsorships',
  templateUrl: './my-account-sponsorships.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SdTitleComponent,
    SdBorderWrapperComponent,
    SdTodoCardComponent,
    SdIconComponent,
    SdHtmlComponent,
    SdCopyToClipboardDirective,
    SdParsePipe,
    SdTruncatePipe,
    SdLoaderComponent
  ]
})
export class MyAccountSponsorshipsComponent implements OnInit, OnDestroy {
  optinCard = signal<ITodoCard>(undefined);
  completedOrderCard = signal<ITodoCard>(undefined);
  hasError = signal<boolean>(false);
  isElligible = signal<boolean>(false);
  referLink = signal<string>(undefined);
  isLoading = signal<boolean>(false);
  isCopied = signal<boolean>(false);

  referAFriend: Record<string, any>;

  BrandThemesEnum = BrandThemesEnum;

  protected readonly subscriptions = new Subscription();

  constructor(
    private readonly myAccount: MyAccountService,
    private readonly userAccountFacade: UserAccountFacade,
    private readonly baseStoreService: BaseStoreService,
    private readonly translate: TranslateService
  ) {
    this.referAFriend = this.translate.instant('referAFriend');
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit(): void {
    const user$ = this.userAccountFacade.get();
    const assets$ = this.myAccount.getAssets();
    const optins$ = this.myAccount.getOptins();

    this.isLoading.set(true);
    this.subscriptions.add(
      combineLatest([user$, assets$, optins$]).subscribe(([user, assets, optins]) => {
        const { market, brandSite } = this.baseStoreService.baseStore;
        const currentMarket = market?.toLowerCase();
        const currentBrand = brandSite?.toLowerCase();
        const isOptin = this.isOptin(optins, currentMarket, currentBrand);
        const hasOrders = !!user?.['completedOrderCount'] || this.hasRegistererdProduct(assets, currentMarket, currentBrand);

        if (isOptin && hasOrders) {
          this.getReferalLink(user.firstName);
          this.completedOrderCard.set(undefined);
          this.optinCard.set(undefined);
          this.isElligible.set(true);
        } else {
          this.completedOrderCard.set(this.getOrdersCard(hasOrders));
          this.optinCard.set(this.getOptinCard(isOptin));
          this.isElligible.set(false);
        }
        this.isLoading.set(false);
      })
    );
  }

  private getReferalLink(userName: string): void {
    this.isLoading.set(true);
    this.subscriptions.add(
      this.myAccount.getReferalLink(userName).pipe(catchError(() => {
        this.hasError.set(true);
        this.isLoading.set(false);
        return of(undefined);
      })).subscribe((res) => {
        this.isLoading.set(false);
        this.referLink.set(res?.encodedURL);
      })
    );
  }

  private getOrdersCard(isCompleted: boolean): ITodoCard {
    const { status, bloc } = this.referAFriend;

    return {
      status: isCompleted ? ITodoCardStatus.success : ITodoCardStatus.pending,
      tagLabel: isCompleted ? status?.done : status?.notDone,
      description: bloc?.purchase.description,
      title: bloc?.purchase.title,
      link: {
        url: !isCompleted ? bloc?.purchase.cta.link : undefined,
        linkName: !isCompleted ? bloc?.purchase.cta.label : ''
      },
      image: {
        url: TODO_CARD_PURCHASE_IMG
      }
    };
  }

  private getOptinCard(isCompleted: boolean): ITodoCard {
    const { status, bloc } = this.referAFriend;

    return {
      status: isCompleted ? ITodoCardStatus.success : ITodoCardStatus.pending,
      tagLabel: isCompleted ? status?.done : status?.notDone,
      description: bloc?.newsletter.description,
      title: bloc?.newsletter.title,
      link: {
        url: !isCompleted ? bloc?.newsletter.cta.link : undefined,
        linkName: !isCompleted ? bloc?.newsletter.cta.label : ''
      },
      image: {
        url: TODO_CARD_NEWSLETTER_IMG
      }
    };
  }

  private hasRegistererdProduct(assets: Asset[] = [], targetMarket: string, targetBrand: string): boolean {
    return !!assets.find(({ market, brand }) => market?.toLowerCase() === targetMarket && brand?.toLowerCase() === targetBrand);
  }

  private isOptin(optins: Optin[], market: string, brand: string): boolean {
    if (!optins?.length) {
      return false;
    }
    return !!optins.find(({ isActive, type, market: m, brand: b }) => (
      isActive && (type === 'Newsletter') && (m.toLowerCase() === market) && (b.toLowerCase() === brand)));
  }

  linkIsCopied(event: string) {
    if (event?.length > 0) {
      this.isCopied.set(true);

      setTimeout(() => {
        this.isCopied.set(false);
      }, 5000);
    }
  }
}
